import React, { useEffect, useState } from "react";
import { Container, Table, Form } from "react-bootstrap";
import AddGlobalExchangeModal from "./AddGlobalExchangeModal";
import GlobalExchangeTableEdit from "./GlobalExchangeTableEdit";
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import Filter from "./Filter";
import PaginationControl from "../calculations/PaginationControl";
import { FaEdit, FaTrash } from "react-icons/fa";

const GlobalExchangeTable = () => {
    const [exchanges, setExchanges] = useState([]);
    const [order, setOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 10;
    const [editExchangeData, setEditExchangeData] = useState(null);

    const fetchExchanges = async (sortOrder = "desc") => {
        try {
            setLoading(true);
            const q = query(collection(db, "globalExchange"), orderBy("createdAt", sortOrder));
            const snapshot = await getDocs(q);
            const exchangeList = snapshot.docs.map((doc) => ({
                docId: doc.id,
                ...doc.data(),
            }));
            setExchanges(exchangeList);
        } catch (error) {
            console.error("Error fetching global exchanges:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExchanges(order);
    }, [order]);

    const handleFilterChange = (e) => {
        setOrder(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusChange = async (docId, newStatus) => {
        try {
            const exchangeRef = doc(db, "globalExchange", docId);
            await updateDoc(exchangeRef, {
                status: newStatus.toLowerCase()
            });
            setExchanges((prev) =>
                prev.map((exchange) =>
                    exchange.docId === docId ? { ...exchange, status: newStatus.toLowerCase() } : exchange
                )
            );
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentExchanges = exchanges.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(exchanges.length / itemsPerPage);

    return (
        <section className="px-1 py-4 nav-body-bg btm-rad">
            <AddGlobalExchangeModal />
            <Container>
                <div className="row align-items-center justify-content-between flex-column flex-md-row text-center text-md-start">
                    <div className="col-auto mb-3 mb-md-0">
                        <h3 className="text-h2">Global Exchange Rates</h3>
                    </div>

                    <div className="col-auto">
                        <div className="d-flex align-items-center gap-2">
                            <Filter value={order} onChange={handleFilterChange} />
                            <button
                                className="btn btn-primary px-3"
                                style={{ padding: "3px 0px" }}
                                data-bs-toggle="modal"
                                data-bs-target="#addGlobalExchangeModal"
                            >
                                + Add Exchange Rate
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive scrollable-table-y mt-4 bg-transparent" style={{ minHeight: '100vh', overflowY: 'auto' }}>
                    <style>
                        {`
                            @media (max-width: 768px) {
                                .scrollable-table-y {
                                    max-height: 300px;
                                    overflow-y: auto;
                                }
                            }
                        `}
                    </style>

                    <Table className="table-hover align-middle body-text-two mb-0">
                        <thead className="sticky-top top-0" style={{ zIndex: 10, background: "transparent !important" }}>
                            <tr>
                                <th className="transparent-header">SL</th>
                                <th className="text-center transparent-header">Receiver's Country</th>
                                <th className="text-center transparent-header">USD Amount</th>
                                <th className="text-center transparent-header">Exchange Rate</th>
                                <th className="text-center transparent-header">Status</th>
                                <th className="text-center transparent-header">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 transparent-header">
                                        <div className="admin-loader"></div>
                                    </td>
                                </tr>
                            ) : currentExchanges.length > 0 ? (
                                currentExchanges
                                    .slice()
                                    .reverse()
                                    .map((exchange, index) => (
                                        <tr key={exchange.docId || index} style={{ borderBottom: "1px solid #8CB6EF" }}>
                                            <td className="py-3 transparent-header text-center">{index + 1}</td>
                                            <td className="py-3 transparent-header text-center">{exchange.receiverCountry}</td>
                                            <td className="py-3 transparent-header text-center">
                                                {exchange.amount !== undefined ? `${exchange.amount.toFixed(2)} USD` : "-"}
                                            </td>
                                            <td className="py-3 transparent-header text-center">
                                                {exchange.fxAmount !== undefined
                                                    ? `${exchange.fxAmount.toFixed(2)} ${exchange.receiverCurrency}`
                                                    : "-"}
                                            </td>
                                            <td className="py-3 transparent-header text-center">
                                                <Form.Select
                                                    value={exchange.status?.toLowerCase() || "inactive"}
                                                    onChange={(e) => handleStatusChange(exchange.docId, e.target.value)}
                                                    size="sm"
                                                    className={`transparent-header border-0 ${exchange.status?.toLowerCase() === "active" ? "text-success" : "text-danger"}`}
                                                    style={{ width: "100px", margin: "0 auto" }}
                                                >
                                                    <option value="active" className="text-dark">Active</option>
                                                    <option value="inactive" className="text-dark">Inactive</option>
                                                </Form.Select>
                                            </td>
                                            <td className="py-4 transparent-header text-center d-flex justify-content-center gap-3">
                                                <FaEdit
                                                    className="text-warning"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setEditExchangeData(exchange)}
                                                />
                                                <FaTrash
                                                    className="text-danger"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={async () => {
                                                        if (window.confirm("Are you sure you want to delete this exchange rate?")) {
                                                            await deleteDoc(doc(db, "globalExchange", exchange.docId));
                                                            fetchExchanges();
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 transparent-header">
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                <div className="d-flex justify-content-center mt-4">
                    <PaginationControl totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
            </Container>
            <GlobalExchangeTableEdit
                exchange={editExchangeData}
                setExchange={setEditExchangeData}
                fetchExchanges={fetchExchanges}
            />
        </section>
    );
};

export default GlobalExchangeTable;
